import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Heart, Sparkles, Award, Users, Package, MapPin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const AboutPage = () => {
  const stats = [
    { icon: Users, value: '2,000+', label: 'Happy Customers' },
    { icon: Package, value: '5,000+', label: 'Orders Shipped' },
    { icon: Award, value: '4.9★', label: 'Customer Rating' },
    { icon: MapPin, value: '100%', label: 'Australian Made' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-teal-50">
      <Helmet>
        <title>About Us | Charming Creations - Handcrafted Resin Art Australia</title>
        <meta name="description" content="Discover the story behind Charming Creations. We're an Australian small business creating beautiful handcrafted resin art jewellery and gifts with love and passion." />
        <link rel="canonical" href="https://www.charmingcreationsgifts.com/about" />
        <meta property="og:title" content="About Charming Creations | Our Story" />
        <meta property="og:description" content="Australian small business creating handcrafted resin art jewellery and gifts." />
      </Helmet>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-amber-200 shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_be17761e-04ff-4edf-8e1f-841eccc40e55/artifacts/she6yj1e_IMG_6151.png" 
                alt="Charming Creations Logo" 
                className="w-12 h-12 object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold bg-gradient-to-r from-amber-600 to-teal-600 bg-clip-text text-transparent">
                  Charming Creations
                </h1>
              </div>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-amber-300 hover:bg-amber-50">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-amber-500 to-teal-500 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <Heart className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Our Story</h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto">
            Creating beautiful handcrafted resin art jewellery and gifts, made with love in Australia
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <stat.icon className="w-10 h-10 mx-auto text-amber-500 mb-3" />
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Story */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                <Sparkles className="inline w-8 h-8 text-amber-500 mr-2" />
                How It All Began
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Charming Creations started as a passion project born from a love of creating beautiful, unique pieces that bring joy to people's lives.
                </p>
                <p>
                  What began as a hobby quickly grew into something more as friends, family, and then strangers started falling in love with our handcrafted resin art pieces.
                </p>
                <p>
                  Today, we're proud to be an Australian small business, creating everything by hand in our home studio with the same care and attention to detail as day one.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://cdn.shopify.com/s/files/1/0746/9442/6899/files/Photoroom_20251229_114606_360x.png?v=1767002347"
                alt="Handcrafted resin art"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Values */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Believe In</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border-2 border-amber-200 hover:border-amber-300 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Made with Love</h3>
                  <p className="text-gray-600 text-sm">
                    Every piece is handcrafted with care and attention to detail. We pour our heart into everything we create.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-teal-200 hover:border-teal-300 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-teal-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Unique & Original</h3>
                  <p className="text-gray-600 text-sm">
                    No two pieces are exactly alike. Each creation has its own character and charm that makes it special.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-2 border-purple-200 hover:border-purple-300 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Quality First</h3>
                  <p className="text-gray-600 text-sm">
                    We use only premium materials and take the time to ensure every piece meets our high standards.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-r from-amber-100 to-teal-100 rounded-2xl p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Ready to Find Your Perfect Piece?
            </h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              Browse our collection of handcrafted resin art jewellery and homewares. Each piece is waiting to bring a little magic into your life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/jewellery">
                <Button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8">
                  Shop Jewellery
                </Button>
              </Link>
              <Link to="/homewares">
                <Button variant="outline" className="border-2 border-teal-500 text-teal-600 hover:bg-teal-50 px-8">
                  Shop Homewares
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
